import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {Button} from 'primeng/button';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {InputOtpModule} from 'primeng/inputotp';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {environment} from '../../../../../environments/environment';
import {VerificationService} from '../../../../services/usermanagement/verification.service';
import {RestErrorHelper} from '../../../../helper/rest-error-helper';
import {TooltipModule} from 'primeng/tooltip';
import {ViewService} from '../../../shared/service/view.service';
import {ConfirmationService} from 'primeng/api';
import {MailReturn} from '../../messages-module/model/mail-return';
import {EMessageType, MessageComponent} from '../../../../components/generic/message/message.component';
import {ErrorType} from '../../../../model/error-type';

@Component({
  selector: 'app-verify-mail',
  standalone: true,
  imports: [
    FormsModule,
    InputOtpModule,
    ReactiveFormsModule,
    TranslatePipe,
    Button,
    TooltipModule,
    MessageComponent
  ],
  templateUrl: './verify-mail.component.html',
  styleUrl: './verify-mail.component.scss'
})
export class VerifyMailComponent implements OnInit, OnChanges
{
  public view = inject(ViewService);
  private verificationService = inject(VerificationService);
  private restErrorHelper = inject(RestErrorHelper);
  private confirmationService = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  @Output() onVerified = new EventEmitter<any>();

  @Input() mailAddress: string;
  @Output() mailAddressChange = new EventEmitter<string>();
  @Input() mailConfirmationCode: string;
  @Output() mailConfirmationCodeChange = new EventEmitter<string>();

  @Input() showInputAlways: boolean;
  @Input() showMailVerificationTip: boolean;
  @Input() showMailVerificationTipColor: string;
  @Input() placeholder: string;
  @Input() baseUrl = environment.baseUrlUsermanagement;
  @Input() mailReturn = false;

  isVerifyingMail: boolean;
  isVerifyingMailCode: boolean;

  mailVerificationCode: string;
  mailVerified: boolean;
  verificationMailSent: boolean;

  mailStatusIcon: string;
  mailStatusTooltip: string;
  eventSource: EventSource;
  mailReturnMessage: ErrorType;

  ngOnInit(): void
  {
    this.initMailStatus();
  }

  ngOnChanges(changes: SimpleChanges): void
  {
    // console.log('ngOnChanges, ', changes);
    if (changes.mailAddress?.firstChange && changes.mailAddress.currentValue)
      this.requestConfirmationCode();
  }

  private initMailStatus(): void
  {
    if (this.mailVerified)
    {
      this.mailStatusIcon = 'pi pi-verified green';
      this.mailStatusTooltip = 'MAIL_ADDRESS_VERIFIED';
    }
    else if (this.verificationMailSent)
    {
      this.mailStatusIcon = 'pi pi-hourglass orange';
      this.mailStatusTooltip = 'VERIFY_MAIL_ADDRESS';
    }
    else
    {
      this.mailStatusIcon = 'pi pi-times red';
      this.mailStatusTooltip = 'NOT_VERIFIED';
    }
  }

  public requestConfirmationCode(): void
  {
    if (this.isVerifyingMail || this.mailVerified || this.verificationMailSent)
      return;

    this.isVerifyingMail = true;
    this.mailReturnMessage = undefined;

    this.verificationService.verifyMail(this.baseUrl, this.mailAddress).subscribe({
      next: () =>
      {
        console.log('Verification Code gesendet');
        this.isVerifyingMail = false;
        this.restErrorHelper.showInfoMessageService('VERIFICATION_MAIL_SENT_MSG', environment.toastDurationLong);
        this.verificationMailSent = true;
        this.subscribeToSse();
        this.initMailStatus();
      },
      error: error =>
      {
        this.isVerifyingMail = false;
        if (this.checkMailVerfiedError(error))
          this.restErrorHelper.handleErrorService(error);
        this.initMailStatus();
      }
    });
  }

  public verificationCodeChanged(): void
  {
    console.log('verificationCodeChanged, value: ' + this.mailVerificationCode);
    if (!this.isVerifyingMailCode && this.mailVerificationCode?.length === 6)
    {
      this.isVerifyingMailCode = true;
      this.verificationService.verifyMailCode(this.baseUrl, this.mailAddress, this.mailVerificationCode).subscribe({
        next: data =>
        {
          console.log('Verification Code überprüft', data);
          this.isVerifyingMailCode = false;
          this.restErrorHelper.showInfoMessageService('MAIL_ADDRESS_VERIFIED', environment.toastDurationShort);
          this.mailVerified = true;
          this.mailConfirmationCode = this.mailVerificationCode;
          this.mailConfirmationCodeChange.emit(this.mailConfirmationCode);
          this.onVerified.emit();
          this.initMailStatus();
          this.closeSse();
        },
        error: error =>
        {
          this.isVerifyingMailCode = false;
          if (this.checkMailVerfiedError(error))
            this.restErrorHelper.handleErrorService(error);
          this.initMailStatus();
        }
      });
    }
  }

  private checkMailVerfiedError(error: any): boolean
  {
    if (error?.error?.message === 'MAIL_ALREADY_VERIFIED')
    {
      this.mailVerified = true;
      this.onVerified.emit();
      return false;
    }
    return true;
  }

  public resetMail(): void
  {
    this.confirmationService.confirm({
      message: this.translate.instant('MSG_RESET_MAIL_ADDRESS'),
      header: this.translate.instant('RESET_MAIL_ADDRESS'),
      icon: 'pi pi-question-circle',
      acceptLabel: this.translate.instant('YES'),
      rejectLabel: this.translate.instant('NO'),
      rejectButtonStyleClass: 'button-no button-right',
      acceptButtonStyleClass: '',
      accept: () =>
      {
        this.mailVerified = false;
        this.verificationMailSent = false;
        this.mailVerificationCode = '';
        this.mailAddress = '';
        this.mailAddressChange.emit(this.mailAddress);
        this.mailConfirmationCode = '';
        this.mailConfirmationCodeChange.emit(this.mailConfirmationCode);
        this.closeSse();
        this.initMailStatus();
      }
    });
  }

  public trimField(): void
  {
    if (this.mailAddress?.length > 0)
      this.mailAddress = this.mailAddress.trim();
    // this.dataChanged();
  }

  private subscribeToSse(): void
  {
    console.log('subscribeToSse, mailReturn: ' + this.mailReturn + ', mail: ' + this.mailAddress);
    if (!this.mailReturn)
      return;

    if (!this.eventSource && this.mailAddress)
    {
      const url = this.baseUrl + 'sse/mail/' + encodeURIComponent(this.mailAddress);
      console.log('subscribeToSse (verifyMail PlayerProfile): ' + url);
      this.eventSource = new EventSource(url);

      this.eventSource.addEventListener('update', event =>
      {
        const data = JSON.parse(event.data);
        console.log('SSE Update empfangen', data);
        if (data && data.objectValue && data.objectName === 'verificationgeneric')
          this.serverSentEventReceived(data);
      });

      this.destroyRef.onDestroy(() => this.closeSse());
      window.addEventListener('beforeunload', () => this.closeSse());
    }
  }

  private closeSse(): void
  {
    if (this.eventSource)
    {
      console.log('closeSse()', this.eventSource);
      this.eventSource.close();
      this.eventSource = undefined;
    }
  }

  private serverSentEventReceived(mailReturn: MailReturn): void
  {
    if (this.mailAddress === mailReturn.objectValue)
    {
      this.mailReturnMessage = new ErrorType();
      this.mailReturnMessage.closable = false;
      this.mailReturnMessage.header = this.translate.instant('PROBLEMS_SNEDING_THE_EMAIL');
      this.mailReturnMessage.message = MailReturn.getText(mailReturn, this.translate);
      this.mailReturnMessage.severity = EMessageType.warn;
    }
  }
}
